import { Component, type ErrorInfo, type ReactNode } from 'react'

type Props = { children: ReactNode }
type State = { hasError: boolean }

/**
 * Last-resort crash fallback. Uses inline styles on purpose: it must render even
 * when the app's CSS, design tokens, or providers are the thing that failed, so
 * it can't depend on Tailwind/i18n being available. A render crash becomes a
 * recoverable "reload" card instead of a white screen.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console -- last-resort visibility for a render crash
    console.error('[polf] render crash', error, info.componentStack)
  }

  render() {
    const { hasError } = this.state
    const { children } = this.props
    if (!hasError) return children
    return (
      <div
        role="alert"
        style={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          padding: 24,
          textAlign: 'center',
          fontFamily: 'system-ui, sans-serif',
          color: '#1a1a1a',
          background: '#f5f1e8',
        }}
      >
        <p style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>
          Noe gikk galt · Something went wrong
        </p>
        <p style={{ fontSize: 14, margin: 0, maxWidth: 360 }}>
          Prøv å laste inn siden på nytt. · Try reloading the page.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            minHeight: 44,
            padding: '0 20px',
            borderRadius: 10,
            border: 'none',
            background: '#1a1a1a',
            color: '#fff',
            fontSize: 15,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Last inn på nytt · Reload
        </button>
      </div>
    )
  }
}

export default ErrorBoundary
